import React, { useCallback, useEffect, useState } from 'react';

import { useToast } from '@/hooks';
import {
  CALENDAR_MONTHS_DISPLAY_RANGE_FUTURE,
  CALENDAR_MONTHS_DISPLAY_RANGE_PAST
} from '@/utils/constants';
import { now } from '@/utils/helpers/now';
import {
  addDays,
  addMonths,
  endOfWeek,
  format,
  isSameDay,
  isToday,
  startOfDay,
  startOfWeek,
  subMonths
} from 'date-fns';
import { Carousel } from 'react-responsive-carousel';

import './DatePicker.css';

interface DatePickerProps {
  start?: Date | null;
  end?: Date | null;
  value?: Date | null;
  onSelect: (date: Date) => void;
  onSetMonth: (month: string) => void;
}

const DatePicker: React.FC<DatePickerProps> = ({
  start,
  end,
  value,
  onSelect,
  onSetMonth
}) => {
  const displayToast = useToast();

  const [currentWeekIndex, setCurrentWeekIndex] = useState<number>(0);
  const [weeks, setWeeks] = useState<Date[][]>([]);

  const onDateClick = (date: Date) => {
    onSelect(date);
  };

  const generateWeeks = useCallback(async () => {
    setWeeks([]);

    const defaultStartDate = startOfWeek(
      subMonths(now(), CALENDAR_MONTHS_DISPLAY_RANGE_PAST)
    );
    const defaultEndDate = endOfWeek(
      addMonths(now(), CALENDAR_MONTHS_DISPLAY_RANGE_FUTURE)
    );

    const newWeeks = [];
    let currentDate = start ? start : defaultStartDate;
    const endDate = end ? end : defaultEndDate;

    while (currentDate <= endDate) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        if (currentDate <= endDate) {
          week.push(currentDate);
          currentDate = addDays(currentDate, 1);
        }
      }
      newWeeks.push(week);
    }

    setWeeks(newWeeks);
  }, [start, end]);

  useEffect(() => {
    generateWeeks();
  }, [generateWeeks]);

  useEffect(() => {
    if (weeks.length > 0) {
      const newValue = value || startOfDay(now());
      const valueWeekIndex = weeks.findIndex((week) =>
        week.some((date) => isSameDay(date, newValue))
      );

      if (weeks[valueWeekIndex]?.length > 0) {
        setCurrentWeekIndex(valueWeekIndex);
        onSetMonth(format(weeks[valueWeekIndex][3], 'MMMM yyyy'));
      }
    }
  }, [weeks, value]);

  const onSlide = (index: number) => {
    if (weeks[index]?.length > 0) {
      setCurrentWeekIndex(index);
      onSetMonth(format(weeks[index][3], 'MMMM yyyy'));
    }

    if (index === 0 || index === weeks.length - 1) {
      displayToast({
        message: `You've reached the end of the calendar. You can only view dates within a ${CALENDAR_MONTHS_DISPLAY_RANGE_FUTURE}-month range. Please contact support to request data beyond this range.`,
        duration: 7000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  };

  return (
    <Carousel
      className="date-picker"
      selectedItem={currentWeekIndex}
      autoPlay={false}
      infiniteLoop={false}
      onChange={onSlide}
      showArrows={false}
      showStatus={false}
      showThumbs={false}
      showIndicators={false}
      useKeyboardArrows={true}
      emulateTouch={true}
    >
      {weeks.map((week, index) => (
        <div className="date-picker__week-slide" key={`week-${index}`}>
          {week.map((date) => (
            <div
              key={date.toISOString()}
              className={`date-picker__item ${value && isSameDay(date, value) ? 'date-picker__item--selected' : ''} ${isToday(date) ? 'date-picker__item--today' : ''}`}
              data-date={date.toISOString()}
              onClick={() => onDateClick(date)}
            >
              <div className="date-picker__day-label">
                {format(date, 'EEE')}
              </div>
              <div className="date-picker__date-label">{format(date, 'd')}</div>
            </div>
          ))}
        </div>
      ))}
    </Carousel>
  );
};

export default DatePicker;
